import { ReactNode } from 'react';

interface MessagePageProps {
  message: ReactNode;
}

/**
 * ISSUE-042: `100%`, not `100vh`. Both routed consumers resolve the percentage
 * against `.sup-container` (`height: calc(100vh - 6.75rem)`, App.css:4-7) — the
 * ErrorBoundary fallback because it renders in that layout route, and the
 * /error route because App.tsx wraps it in the same class precisely to supply a
 * definite height. `100vh` overflowed the VIEWPORT by the header's height.
 *
 * Be precise about WHY it fits, because it is not the obvious reason.
 * `.sup-container` CONTAINS the header (Container.tsx: header + Outlet are
 * siblings inside it) rather than sitting below it, so this box at `height:
 * 100%` is the container's full height and therefore overflows the container's
 * own box by the header's 3.375rem. It fits the viewport anyway only because
 * App.css subtracts 6.75rem == 2 x 3.375rem: header (43.2px) + box
 * (100vh - 86.4px) ends at 100vh - 43.2px, and the box's centre lands at
 * exactly 50vh. That identity rests on two constants in two files — App.css:5
 * and StyledHeader.tsx:9 — with nothing asserting they stay in step. Change the
 * header height alone and this silently mis-centres and then overflows.
 *
 * The one chain with no definite-height parent is the last-resort boundary in
 * `src/index.tsx`, whose fallback hangs off #root: there the percentage
 * computes to `auto`, so the box is content-sized and sits at the top of the
 * page instead of centred. Nothing is clipped and nothing overflows — it just
 * stops claiming a viewport it is not the only occupant of.
 */
const MessagePage = ({ message }: MessagePageProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <div>{message}</div>
    </div>
  );
};

export default MessagePage;
